import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, ShieldAlert, ShieldCheck, Clock, TrendingUp, Activity, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const SEV_COLORS = { CRITICA: '#ef4444', ALTA: '#f97316', MEDIA: '#eab308', BAJA: '#22c55e' };
const SEV_ORDER = ['CRITICA', 'ALTA', 'MEDIA', 'BAJA'];

export const AlertsDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await api.get('/alerts/dashboard-stats');
      setStats(res.data);
    } catch (err) {
      console.error('Error loading dashboard stats', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!stats) return null;

  const sevData = SEV_ORDER
    .filter(s => stats.severidad[s])
    .map(s => ({ name: s, value: stats.severidad[s], fill: SEV_COLORS[s] }));

  const typeData = (stats.tipos || []).slice(0, 6).map(t => ({
    name: t.tipo.length > 25 ? t.tipo.substring(0, 22) + '...' : t.tipo,
    alertas: t.count
  }));

  const trendData = (stats.tendencia_semanal || []).map(t => ({
    fecha: t.fecha ? t.fecha.substring(5) : '',
    alertas: t.alertas
  }));

  const sla = stats.sla || {};

  return (
    <div data-testid="alerts-dashboard" className="space-y-4 mb-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0" data-testid="kpi-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Total Alertas</p>
              <p className="text-2xl font-bold mt-1">{stats.total?.toLocaleString()}</p>
            </div>
            <Activity className="h-8 w-8 text-slate-500" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-600 to-red-700 text-white border-0" data-testid="kpi-open">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-red-200 uppercase tracking-wider">Abiertas</p>
              <p className="text-2xl font-bold mt-1">{stats.total_abiertas?.toLocaleString()}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-300" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0" data-testid="kpi-closed">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-200 uppercase tracking-wider">Cerradas</p>
              <p className="text-2xl font-bold mt-1">{stats.total_cerradas?.toLocaleString()}</p>
            </div>
            <ShieldCheck className="h-8 w-8 text-emerald-300" />
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-600 to-amber-700 text-white border-0" data-testid="kpi-new-today">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-amber-200 uppercase tracking-wider">Nuevas Hoy</p>
              <p className="text-2xl font-bold mt-1">{stats.nuevas_hoy || 0}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-amber-300" />
          </div>
        </Card>
      </div>

      {/* SLA Semaphore */}
      <Card className="p-4 border border-slate-200" data-testid="sla-semaphore">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" /> SLA de Alertas
        </h3>
        <div className="flex gap-4">
          <div className="flex-1 bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
            <div className="w-3 h-3 rounded-full bg-emerald-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-emerald-700">{sla.verde || 0}</p>
            <p className="text-xs text-emerald-600">&lt; 24h</p>
          </div>
          <div className="flex-1 bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
            <div className="w-3 h-3 rounded-full bg-amber-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-700">{sla.amarillo || 0}</p>
            <p className="text-xs text-amber-600">24-72h</p>
          </div>
          <div className="flex-1 bg-red-50 rounded-lg p-3 text-center border border-red-200">
            <div className="w-3 h-3 rounded-full bg-red-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-700">{sla.rojo || 0}</p>
            <p className="text-xs text-red-600">&gt; 72h</p>
          </div>
        </div>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Severity Pie */}
        {sevData.length > 0 && (
          <Card className="p-4 border border-slate-200" data-testid="chart-severity">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" /> Por Severidad
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={sevData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2}>
                  {sevData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString()} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Weekly Trend */}
        {trendData.length > 0 && (
          <Card className="p-4 border border-slate-200" data-testid="chart-trend">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Tendencia Semanal
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="alertas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Type Distribution */}
        {typeData.length > 0 && (
          <Card className="p-4 border border-slate-200" data-testid="chart-types">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Por Tipo
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={typeData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={100} />
                <Tooltip />
                <Bar dataKey="alertas" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
};
